import React, { memo, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { SessionExpiredModal } from '~/components/SessionExpiredModal';
import { SessionTimeoutModal } from '~/components/SessionTimeoutModal';
import { useAnalytics, useAppDispatch } from '~/hooks';
import { updateSessionExtended } from '~/slices/analytics/trackAnalyticsSlice';
import { qalTrackEventAction } from '~/thunks/analytics';

const ONE_MIN = 60 * 1000; // 60sec

interface IdleUserTimerProps {
  timeoutMinutes: number;
  countdownMinutes: number;
}

const IdleUserTimer: React.FC<IdleUserTimerProps> = ({ timeoutMinutes, countdownMinutes }) => {
  const [isSessionTimeoutOpen, setIsSessionTimeoutOpen] = useState(false);
  const [isSessionExpiredOpen, setIsSessionExpiredOpen] = useState(false);

  const { sessionTimeout } = useAnalytics();
  const { sessionExtended } = sessionTimeout;
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Google Analytics tracks count down completed.
    if (isSessionExpiredOpen) {
      dispatch(
        qalTrackEventAction({
          event: 'session_timeout',
          eventAction: 'modal: close',
          eventLabel: 'session timeout count down completed',
        })
      );
    }
  }, [dispatch, isSessionExpiredOpen]);

  const onIdle = () => {
    setIsSessionTimeoutOpen(true);
  };

  const reloadPortal = () => {
    window.location.reload();
  };

  const onCountdownComplete = () => {
    setIsSessionTimeoutOpen(false);
    setIsSessionExpiredOpen(true);
  };

  const onContinue = () => {
    // Google Analytics tracks how many times user extended the session.
    dispatch(
      qalTrackEventAction({
        event: 'session_timeout',
        eventAction: 'session timeout extended times',
        eventLabel: `${sessionExtended + 1}`,
      })
    );
    dispatch(updateSessionExtended({ sessionExtended: sessionExtended + 1 }));
    setIsSessionTimeoutOpen(false);
    resume();
    start();
  };

  const handleContinue = () => {
    dispatch(
      qalTrackEventAction({
        event: 'session_timeout',
        eventAction: 'modal: close',
        eventLabel: 'Close button - session timeout',
      })
    );
    onContinue();
  };

  const handleExit = () => {
    dispatch(
      qalTrackEventAction({
        event: 'session_timeout',
        eventAction: 'modal: close',
        eventLabel: 'Close button - session timeout',
      })
    );
    setIsSessionTimeoutOpen(false);
    reloadPortal();
  };

  const { start, resume } = useIdleTimer({
    timeout: timeoutMinutes * ONE_MIN,
    onIdle,
    stopOnIdle: true,
  });

  return (
    <>
      <SessionTimeoutModal
        isOpen={isSessionTimeoutOpen}
        countdown={countdownMinutes * ONE_MIN}
        onCountdownComplete={onCountdownComplete}
        onContinue={handleContinue}
        onExit={handleExit}
      />
      <SessionExpiredModal isOpen={isSessionExpiredOpen} onRestart={reloadPortal} />
    </>
  );
};

/** @note : using memo to prevent reload of timer on breakpoint change due device orientation change */
export default memo(IdleUserTimer);
